<template>
  <div>
    <!-- 提示信息1 -->
    <alertStep :useDefault="true" :useDefaultNumber="1" />
    <form-text-box
      ref="rentInfoBox"
      :dataList="rentInfo_formConfig"
      :rules="rentInfo_formRule"
      :model="rentInfo_model"
      :column="2"
    />
  </div>
</template>
<script>
import { getLeasingSchemeConfigDetail } from '@/api/domain/orders.js'
import alertStep from '@/components/rentCalculateConfig/alertStep.vue'
import { CATEGORY_CODE_MAP, CATEGORY_FEE_MAP, FEE_CODE_MAP, PAYMODE_CODE_MAP } from '@/store/dict/rentPlanDict.js'
import { formatNumberWithCommas } from '@/utils'
import * as Sentry from '@sentry/vue'
import moment from 'moment'
import { mapState } from 'vuex'
import rentInfoFn from './config/rentInfo.js'

export default {
  name: 'RentInfo',
  components: { alertStep },
  props: {
    isEnterChildOrder: { // 企业子订单
      type: Boolean,
      default: false,
    },
    defaultRentInfo: {
      type: Object,
      default: () => ({
        rentCategory: '', // 类别
        signDate: '', // 签署日期
        startStopDate: [], // 收款起止日期
        presentDays: '', // 赠送天数
        isTranDeposit: '', // 是否平移原订单押金 1-是 0-否
      }),
    },
    showDeposit: {
      type: Boolean,
      default: false,
    },
    planId: {
      type: [String, Number],
      default: '',
    },
    forbiddenEndDate: {
      type: String,
      default: '',
    },
    deposit: {
      type: Object,
      default: () => ({
        depositAmount: '', // 押金应收
        depositReceived: '', // 已收押金
        flag: '',
        reason: '',
      }),
    },
  },
  data() {
    const { rentInfo_model, rentInfo_formRule } = rentInfoFn.call(this)
    return {
      rentInfo_model,
      rentInfo_formRule,
      disabled: false,
      feeTableData: [],
      rentPlanPeriod: '', // 选择租赁方案的预计租赁周期
      rentPlanPeriodType: null, // 选择租赁方案的周期类型，1：天、3：月
      relationOrderType: 0,
      yaJinObj: {
        fundType: FEE_CODE_MAP.YA_JIN,
        isConfirm: false,
        amount: this.deposit.depositAmount || '',
        payMode: PAYMODE_CODE_MAP.DISPOSABLE, // 一次性
        isSelectScheme: true,
        advancePayment: 2, // 发车前收款：2-收取全部
        items: [
          {
            dueDate: '',
            endCycleDate: '',
            planAmount: this.deposit.depositAmount || '',
            remarks: '',
            startCycleDate: '',
          },
        ],
      },
    }
  },
  watch: {
    defaultRentInfo: {
      handler(val) {
        const { signDate, startStopDate, presentDays, rentCategory, isTranDeposit } = val
        this.rentInfo_model.signDate = signDate || new Date()
        this.rentInfo_model.startStopDate = startStopDate
        this.rentInfo_model.presentDays = presentDays
        this.rentInfo_model.rentCategory = rentCategory
        this.rentInfo_model.isTranDeposit = isTranDeposit
      },
      deep: true,
      immediate: true,
    },
    'defaultRentInfo.rentCategory': {
      handler(val) {
        if (val) {
          this.queryFeeList().then((feeList) => this.addRequiredFee(feeList))
        }
      },
      immediate: true,
    },
    'rentInfo_model.startStopDate': {
      handler(val) {
        if (!val) return
        this.calculateContractDate()
      },
      immediate: true,
    },
    'rentInfo_model.presentDays': {
      handler(val) {
        if (!val) return
        this.calculateContractDate()
      },
    },
    planId: {
      handler(val) {
        val && this.getPlanDetail(val)
      },
      immediate: true,
    },
    'deposit.flag': {
      handler(val) {
        // 如果禁用平移押金选项，则一定是不平移押金，如果不禁用平移押金选项，则可以选是或否
        if (!val) this.rentInfo_model.isTranDeposit = 0
      },
      immediate: true,
    },
    'rentInfo_model.isTranDeposit': {
      handler() {
        if (!this.showDeposit) return
        this.queryFeeList().then((feeList) => this.addRequiredFee(feeList))
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState({
      categoryList: (state) => state.rentPlan.categoryList, // 类别
      orderSignRentFee: (state) => state.rentPlan.orderSignRentFee, // 租车费项
      orderSignPurchaseFee: (state) => state.rentPlan.orderSignPurchaseFee, // 已租代购费项
      orderSignCooperateFee: (state) => state.rentPlan.orderSignCooperateFee, // 司机合作模式费项
    }),
    rentInfo_formConfig() {
      const { rentInfo_formConfig } = rentInfoFn.call(this)
      return rentInfo_formConfig
    },
    categoryFilterList() {
      // 转租赁模式:类别只展示租车
      if (this.relationOrderType === 5) {
        return this.categoryList.filter((i) => i.code === CATEGORY_CODE_MAP.ZU_CHE)
      }
      // 企业子订单，只展示租车和以租代购
      if (this.isEnterChildOrder) {
        return this.categoryList.filter((i) => i.code !== CATEGORY_CODE_MAP.SI_JI_HE_ZUO_MO_SHI)
      }
      return this.categoryList
    },
    /*
      签署日期选择控件的配置项
      签署日期应小于等于收款的开始时间
    */
    signInfoScrapDate() {
      return {
        disabledDate: (time) => {
          const { startStopDate } = this.rentInfo_model
          if (!startStopDate || !startStopDate.length) {
            // 收款起始日期还没选择的情况下，签署日期控件不受限制
            return false
          }
          return moment(time.getTime()).isAfter(moment(startStopDate[0]))
        },
      }
    },
    /*
      收款起止日期选择控件的配置项
      收款起止日期应大于等于签署日期
    */
    startStopInfoScrapDate() {
      if (this.showDeposit || this.isEnterChildOrder) {
        return {
          disabledDate: (time) => {
            return moment(time.getTime()).isBefore(this.availablePaymentStartDate)
          },
          onPick: this.onPick,
        }
      }
      return {
        onPick: this.onPick,
      }
    },
    /*
      下一期最近可用的收款"起始"日期字符串，格式为 yyyy-MM-dd
      因为 上一期的结束日期 < 签署日期 <= 下一期最近可用的收款起始日期
      因此 下一期最近的可用收款起始日期 = max(签署日期, 上一期的结束日期 + 1天)
      如果签署日期和上一期的结束日期都为空，则返回new Date()
    */
    availablePaymentStartDate() {
      // 签署日期（通常，签署日期会在页面加载时选择会当天，所以一般不会为空）
      const { signDate } = this.rentInfo_model
      // 上一期订单的结束日期（上一期的结束日期可能为空，例如在新建订单时），上一期的结束日期包含赠送天数
      const prevOrderEndDate = this.forbiddenEndDate
      if (!signDate && !prevOrderEndDate) {
        return moment().format('yyyy-MM-DD')
      }

      if (!signDate) {
        return moment(prevOrderEndDate).add(1, 'days').format('yyyy-MM-DD')
      }

      if (!prevOrderEndDate) {
        return moment(signDate).format('yyyy-MM-DD')
      }

      return moment(signDate).isAfter(prevOrderEndDate)
        ? moment(signDate).format('yyyy-MM-DD')
        : moment(prevOrderEndDate).add(1, 'days').format('yyyy-MM-DD')
    },
  },
  mounted() {
    // 获取类别list
    if (this.categoryList === null || !this.categoryList.length) {
      this.$store.dispatch('getRentCategory').then(() => {
        this.setCateGoryFn()
      })
    } else {
      this.setCateGoryFn()
    }
    this.queryFeeList().then((feeList) => this.addRequiredFee(feeList))

    // relationOrderType 1-临时换车，2-订单换车，3-订单换人，4-订单续约 5-转租赁
    const { relationOrderType = 0 } = this.$route.query
    this.relationOrderType = Number(relationOrderType)
  },
  methods: {
    // 选择租赁方案后 结束日期=开始日期+预租周期
    onPick(time) {
      if (!this.rentPlanPeriod) {
        // 没有预计租赁周期 不设置结束日期
        return
      }
      this.$nextTick(() => {
        // 先清空在赋值
        this.rentInfo_model.startStopDate = []
        this.rentInfo_model.startStopDate[0] = moment(new Date(time.minDate))
          .startOf('day')
          .format('YYYY-MM-DD HH:mm:ss')
        this.rentInfo_model.startStopDate[1] = moment(new Date(time.minDate))
          .add(this.rentPlanPeriod, this.rentPlanPeriodType === 1 ? 'day' : 'month')
          .subtract(1, 'days')
          .endOf('day')
          .format('YYYY-MM-DD HH:mm:ss')
      })
      // 关闭--时间控件下拉框
      this.$refs.rentInfoBox
        .getRefs('startStopDate')
        .$children[1].$children[0].handleClose()
    },
    // 所有租赁信息清空
    resetFormData() {
      this.rentInfo_model.rentCategory = CATEGORY_CODE_MAP.ZU_CHE
      this.rentInfo_model.startStopDate = []
      this.rentInfo_model.presentDays = ''
      this.disabled = false
      this.rentInfo_model.disabled = false
      this.rentPlanPeriod = ''
      this.rentPlanPeriodType = null
      this.queryFeeList().then((feeList) => this.addRequiredFee(feeList, 'fromEvent'))
    },
    // 设置租赁类别默认值
    setCateGoryFn() {
      if (this.defaultRentInfo.rentCategory) {
        this.rentInfo_model.rentCategory = this.defaultRentInfo.rentCategory
      } else {
        // 默认展示租车
        const someOne = this.categoryList?.find(
          (item) => Number(item.code) === CATEGORY_CODE_MAP.ZU_CHE,
        )
        if (someOne) {
          this.rentInfo_model.rentCategory = someOne.code
        }
        this.updateData()
      }
    },
    // 获取方案详情
    async getPlanDetail(id, isDateChange) {
      const { data = {} } = await getLeasingSchemeConfigDetail({ id })
      if (!isDateChange) {
        this.rentInfo_model.rentCategory = data.rentCategory
        this.rentInfo_model.startStopDate = []
        this.rentInfo_model.startStopDate[0] = moment(new Date())
          .add(1, 'day')
          .startOf('day')
          .format('YYYY-MM-DD HH:mm:ss')
        if (data.rentPeriodType === 3) {
          this.rentInfo_model.startStopDate[1] = moment(new Date())
            .add(data.rentPeriod, 'month')
            .endOf('day')
            .format('YYYY-MM-DD HH:mm:ss')
        } else if (data.rentPeriodType === 1) {
          this.rentInfo_model.startStopDate[1] = moment(new Date())
            .add(data.rentPeriod, 'day')
            .endOf('day')
            .format('YYYY-MM-DD HH:mm:ss')
        }
        this.rentPlanPeriod = data.rentPeriod
        this.rentPlanPeriodType = data.rentPeriodType
        this.rentInfo_model.presentDays = data.presentDays
        this.rentInfo_model.presentDaysText = `${(data.presentDays || 0)}天`
        // 禁用类别和租赁方案
        this.disabled = true
        this.rentInfo_model.disabled = true
      }

      if (!CATEGORY_FEE_MAP[data.rentCategory]) {
        Sentry.captureException('详情接口未返回租赁类别')
        return
      }
      const someFeeList = this[CATEGORY_FEE_MAP[data.rentCategory]]
      if (!someFeeList || !someFeeList.length) {
        this.queryFeeList().then((feeList) =>
          this.setFeeItemArr(feeList, data.orderPlanFeeList))
      } else {
        this.setFeeItemArr(someFeeList, data.orderPlanFeeList)
      }
    },
    setFeeItemArr(feeList, orderPlanFeeList) {
      const newArray = []
      orderPlanFeeList.forEach((item) => {
        const newItem = { ...item }
        const findFee = feeList.find((i) => Number(i.code) === Number(item.type))
        if (findFee) {
          newItem.isConfirm = false
          newItem.toast = findFee.toast // 提示语
          newItem.required = findFee.required // 是否必填费项
          newItem.fundType = item.type // 费项code（押金，租金......）
          newItem.name = item.typeName // 费项名称
          newItem.payMode = item.payMethod // 收款方式
          newItem.isSelectScheme = true // 是否是选的租赁方案
          newArray.push(newItem)
        }
      })
      this.feeTableData = newArray
      this.updateData('fromEvent')
    },
    // 获取费项list
    async queryFeeList() {
      if (!this.rentInfo_model.rentCategory) {
        this.rentInfo_model.rentCategory = CATEGORY_CODE_MAP.ZU_CHE
      }
      const someFeeList =
        this[CATEGORY_FEE_MAP[this.rentInfo_model.rentCategory]]
      if (!someFeeList || !someFeeList.length) {
        const res = await this.$store.dispatch('getFeeList', {
          type: CATEGORY_FEE_MAP[this.rentInfo_model.rentCategory],
        })
        return res.data || []
      }
      return someFeeList
    },
    addRequiredFee(someFeeList, from) {
      // 过滤必填费项
      const requiredFee = someFeeList.filter((item) => item.required) || []
      let formatFees = requiredFee.map((i) => {
        const item = {}
        item.fundType = i.code
        item.name = i.name
        item.required = i.required
        item.toast = i.toast
        item.isConfirm = false
        return item
      })
      // 若类型选的是【以租代购】,(首付款/租金)和(全款)是互斥关系，table默认展示 首付款/租金，全款需过滤掉
      if (Number(this.rentInfo_model.rentCategory) === CATEGORY_CODE_MAP.YI_ZU_DAI_GOU) {
        // 15-全款
        formatFees = formatFees.filter((i) => Number(i.fundType) !== FEE_CODE_MAP.QUAN_KUAN)
      }
      // 若需要平移原定单押金，需配置
      if (this.showDeposit && this.rentInfo_model.isTranDeposit) {
        // 因为订单续约创建新订单时，类别可以自定义选择。而新模式下【以租代购】只有首付款，没有押金。所以在选择确定押金平移时：
        // 1、原订单的押金处理逻辑不变，仍然是生成押金的付款单把账给平了
        // 2、新订单下，把原订单的押金对应金额，对应到费项“首付款”上。
        //  ● 收款方式为一次性，不可编辑
        //  ● 收款金额：平移原订单的押金金额，不可编辑
        //  ● 缴款日：默认为收款起始日，且不可编辑
        //  ● 发车前收款：收取全部，不可编辑
        // 3、列表上仍然展示“以租代购模式下，首付款为必填项。请点击配置首付款的支付计划”的文案，需要操作者点击配置按指引确认完后回填信息。
        // 基于以上产品需求变更，所以如果为以租代购，将押金code转成首付款的code值
        if (Number(this.rentInfo_model.rentCategory) === CATEGORY_CODE_MAP.YI_ZU_DAI_GOU) {
          this.yaJinObj.fundType = FEE_CODE_MAP.SHOU_FU_KUAN
        } else {
          this.yaJinObj.fundType = FEE_CODE_MAP.YA_JIN
        }
        formatFees = formatFees.map((item) => {
          let newItem = item
          if (newItem.fundType === this.yaJinObj.fundType) {
            newItem = Object.assign({}, newItem, this.yaJinObj)
          }
          return newItem
        })
      }
      this.feeTableData = formatFees
      this.updateData(from)
    },
    // 计算账期天数和合同的起止日
    calculateContractDate() {
      let startDate = ''
      let endDate = ''
      let lint = ''
      let totalDays = 0
      const presentDays = this.rentInfo_model.presentDays || 0
      if (
        Array.isArray(this.rentInfo_model.startStopDate) &&
        this.rentInfo_model.startStopDate.length
      ) {
        startDate = this.rentInfo_model.startStopDate[0].split(' ')[0]
        lint = this.rentInfo_model.startStopDate[1].split(' ')[0]
        endDate = presentDays ? moment(lint).add(presentDays, 'days').format('YYYY-MM-DD') : lint

        this.rentInfo_model.contractDateText = `${startDate}~${endDate}`
        totalDays = moment(endDate).diff(moment(startDate), 'days') + 1
        const formateNum = formatNumberWithCommas(totalDays)
        this.rentInfo_model.rentPeriodText = `共 ${formateNum} 天`
      } else {
        this.rentInfo_model.contractDateText = ''
        this.rentInfo_model.rentPeriodText = ''
      }
      // 赠送天数
      this.rentInfo_model.presentDaysText = `${presentDays}天`

      // 更新数据，传递到父组件
      this.updateData()
      return totalDays
    },
    updateData(from) {
      this.$emit('updateInfo', this.rentInfo_model, this.feeTableData, from)
    },
    // 获取表单数据
    getFormData() {
      let returnData = false
      this.$refs.rentInfoBox.getRefs().validate((validate) => {
        if (validate) {
          const date = new Date(this.rentInfo_model.signDate)
          date.setHours(0, 0, 0, 0)
          const signDateTimes = date.getTime()
          const startDateTimes = new Date(this.rentInfo_model.startStopDate[0]).getTime()
          if (signDateTimes > startDateTimes
          ) {
            this.$message.error('签署日期应该小于等于收款起止日')
          } else {
            returnData = {
              rentCategory: this.rentInfo_model.rentCategory || 0, // 租赁类别（租车，已租代购，司机合作模式）
              signDate: moment(this.rentInfo_model.signDate).format(
                'YYYY-MM-DD 00:00:00',
              ), // 签署日期
              startStopDate: this.rentInfo_model.startStopDate, // 收款起止日期
              startDate: this.rentInfo_model.startStopDate[0],
              endDate: this.rentInfo_model.startStopDate[1],
              presentDays: this.rentInfo_model.presentDays, // 赠送天数
              isTranDeposit: this.rentInfo_model.isTranDeposit, // 是否平移原订单押金 1-是 0-否
              cycleDays: this.calculateContractDate(), // 总天数
            }
          }
        } else {
          this.autoAnchorPoint(true)
        }
      })
      return returnData
    },
    // 锚点定位
    autoAnchorPoint(toastFlag) {
      toastFlag &&
        this.$message.error('请先设置“租赁周期”后再配置“租金”账单')
      this.$refs.rentInfoBox.$el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      this.$refs.rentInfoBox.getRefs().validate().catch(() => {}) // 表单校验
    },
  },
}
</script>
<style lang="scss" scoped>
::v-deep .tran-deposit-radio {
  .el-form-item__label {
    width: auto;
    min-width: 150px;
    max-width: 300px;
    white-space: pre-line;
  }
}
</style>
